/**
 * Dashboard Service - Verwaltet alle API-Aufrufe für das Dashboard
 */
import ApiClient from './ApiClient';
import API_ENDPOINTS from '../config/apiEndpoints';

export class DashboardService {
  /**
   * Mitarbeiterdaten abrufen
   * @param {string} token - Access Token
   * @returns {Promise<Object>} Mitarbeiterdaten
   */
  static async getEmployeeData(token) {
    try {
      console.log('DashboardService.getEmployeeData called with token:', token ? 'Present' : 'Missing');
      console.log('URL:', API_ENDPOINTS.EMPLOYEE);
      
      const response = await fetch(API_ENDPOINTS.EMPLOYEE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Request-Headers': '*',
        },
        body: JSON.stringify({ token }),
        redirect: 'follow'
      });
      
      console.log('getEmployeeData response status:', response.status);
      const result = await response.json();
      console.log('getEmployeeData result:', result);
      return result;
    } catch (error) {
      console.error('Error in DashboardService.getEmployeeData:', error);
      throw error;
    }
  }

  /**
   * Timer-Datum abrufen (aktiver Timer)
   * @param {string} token - Access Token
   * @param {string} employeeId - Employee ID
   * @returns {Promise<Object>} Timer-Daten
   */
  static async getTimerDate(token, employeeId) {
    try {
      console.log('DashboardService.getTimerDate called with:', { 
        token: token ? 'Present' : 'Missing', 
        employeeId 
      });
      console.log('URL:', API_ENDPOINTS.GET_TIMER_DATE);
      
      const response = await fetch(API_ENDPOINTS.GET_TIMER_DATE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, employeeId }),
      });
      
      console.log('getTimerDate response status:', response.status);
      const result = await response.json();
      console.log('getTimerDate result:', result);
      return result;
    } catch (error) {
      console.error('Error in DashboardService.getTimerDate:', error);
      throw error;
    }
  }

  /**
   * Timer-Datum hinzufügen (Timer starten)
   * @param {string} token - Access Token
   * @param {string} employeeId - Employee ID
   * @param {string} startDate - Start-Datum/Zeit
   * @param {string} locationName - Standort
   * @param {Object} logUser - Logging-Informationen
   * @returns {Promise<Object>} Ergebnis
   */
  static async addTimerDate(token, employeeId, startDate, locationName = '', logUser = null) {
    try {
      console.log('DashboardService.addTimerDate called with:', { 
        token: token ? 'Present' : 'Missing', 
        employeeId,
        startDate,
        locationName,
        logUser 
      });
      console.log('URL:', API_ENDPOINTS.ADD_TIMER_DATE);
      
      const response = await fetch(API_ENDPOINTS.ADD_TIMER_DATE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Request-Headers': '*',
        },
        body: JSON.stringify({
          token,
          employeeId,
          startDate,
          locationName,
          logUser
        }),
      });
      
      console.log('addTimerDate response status:', response.status);
      const result = await response.json();
      console.log('addTimerDate result:', result);
      return result;
    } catch (error) {
      console.error('Error in DashboardService.addTimerDate:', error);
      throw error;
    }
  }

  /**
   * Timer-Datum löschen (Timer stoppen)
   * @param {string} token - Access Token
   * @param {string} employeeId - Employee ID
   * @param {Object} logUser - Logging-Informationen
   * @returns {Promise<Object>} Ergebnis
   */
  static async deleteTimerDate(token, employeeId, logUser = null) {
    try {
      console.log('DashboardService.deleteTimerDate called with:', { 
        token: token ? 'Present' : 'Missing', 
        employeeId,
        logUser 
      });
      console.log('URL:', API_ENDPOINTS.DELETE_TIMER_DATE);
      
      const response = await fetch(API_ENDPOINTS.DELETE_TIMER_DATE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Request-Headers': '*',
        },
        body: JSON.stringify({
          token,
          employeeId,
          logUser
        }),
      });
      
      console.log('deleteTimerDate response status:', response.status);
      const result = await response.json();
      console.log('deleteTimerDate result:', result);
      return result;
    } catch (error) {
      console.error('Error in DashboardService.deleteTimerDate:', error);
      throw error;
    }
  }

  /**
   * Zeiteinträge abrufen
   * @param {string} token - Access Token
   * @param {string} employeeId - Employee ID
   * @param {string} month - Monat (YYYY-MM Format)
   * @returns {Promise<Array>} Zeiteinträge
   */
  static async getTimeEntries(token, employeeId, month) {
    return await fetch(process.env.REACT_APP_GET_TIMES_V2_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Request-Headers': '*',
      },
      body: JSON.stringify({
        token,
        employeeId,
        month
      }),
      redirect: 'follow'
    }).then(response => response.json());
  }

  /**
   * Zeiteintrag hinzufügen
   * @param {string} token - Access Token
   * @param {Object} timeSlotData - Zeiteintrag-Daten
   * @param {Object} logUser - Logging-Informationen
   * @returns {Promise<Object>} Ergebnis
   */
  static async addTimeEntry(token, timeSlotData, logUser = null) {
    return await fetch(process.env.REACT_APP_ADD_TIME_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Request-Headers': '*',
      },
      body: JSON.stringify({
        token,
        timeSlotData,
        logUser
      }),
      redirect: 'follow'
    }).then(response => response.json());
  }

  /**
   * Änderungsantrag erstellen
   * @param {string} token - Access Token
   * @param {Object} changeRequestData - Änderungsantrag-Daten
   * @returns {Promise<Object>} Ergebnis
   */
  static async addChangeRequest(token, changeRequestData) {
    return await fetch(process.env.REACT_APP_ADD_CHANGE_REQUEST_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Request-Headers': '*',
      },
      body: JSON.stringify(changeRequestData),
      redirect: 'follow'
    }).then(response => response.json());
  }

  /**
   * Kunden abrufen
   * @param {string} token - Access Token
   * @returns {Promise<Array>} Kunden
   */
  static async getClients(token) {
    try {
      console.log('DashboardService.getClients called with token:', token ? 'Present' : 'Missing');
      console.log('URL:', process.env.REACT_APP_GET_CLIENTS_URL);
      
      const response = await fetch(process.env.REACT_APP_GET_CLIENTS_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });
      
      console.log('getClients response status:', response.status);
      console.log('getClients response ok:', response.ok);
      
      const result = await response.json();
      console.log('getClients result:', result);
      console.log('getClients result type:', typeof result);
      console.log('getClients result length:', Array.isArray(result) ? result.length : 'Not an array');
      
      return result;
    } catch (error) {
      console.error('Error in DashboardService.getClients:', error);
      throw error;
    }
  }

  /**
   * Projekte abrufen
   * @param {string} token - Access Token
   * @returns {Promise<Array>} Projekte
   */
  static async getProjects(token) {
    try {
      console.log('DashboardService.getProjects called with token:', token ? 'Present' : 'Missing');
      console.log('URL:', process.env.REACT_APP_GET_PROJECTS_URL);
      
      const response = await fetch(process.env.REACT_APP_GET_PROJECTS_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });
      
      console.log('getProjects response status:', response.status);
      console.log('getProjects response ok:', response.ok);
      
      const result = await response.json();
      console.log('getProjects result:', result);
      console.log('getProjects result type:', typeof result);
      console.log('getProjects result length:', Array.isArray(result) ? result.length : 'Not an array');
      
      return result;
    } catch (error) {
      console.error('Error in DashboardService.getProjects:', error);
      throw error;
    }
  }

  /**
   * Aufgaben abrufen
   * @param {string} token - Access Token
   * @returns {Promise<Array>} Aufgaben
   */
  static async getTasks(token) {
    try {
      console.log('DashboardService.getTasks called with token:', token ? 'Present' : 'Missing');
      console.log('URL:', process.env.REACT_APP_GET_TASKS_URL);
      
      const response = await fetch(process.env.REACT_APP_GET_TASKS_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });
      
      console.log('getTasks response status:', response.status);
      console.log('getTasks response ok:', response.ok);
      
      const result = await response.json();
      console.log('getTasks result:', result);
      console.log('getTasks result type:', typeof result);
      console.log('getTasks result length:', Array.isArray(result) ? result.length : 'Not an array');
      
      return result;
    } catch (error) {
      console.error('Error in DashboardService.getTasks:', error);
      throw error;
    }
  }

  /**
   * Zeitklassen abrufen
   * @returns {Promise<Array>} Zeitklassen
   */
  static async getTimeClasses() {
    try {
      console.log('DashboardService.getTimeClasses called');
      console.log('URL:', process.env.REACT_APP_GET_TIME_CLASSES_URL);
      
      const response = await fetch(process.env.REACT_APP_GET_TIME_CLASSES_URL);
      
      console.log('getTimeClasses response status:', response.status);
      console.log('getTimeClasses response ok:', response.ok);
      
      const result = await response.json();
      console.log('getTimeClasses result:', result);
      console.log('getTimeClasses result type:', typeof result);
      console.log('getTimeClasses result length:', Array.isArray(result) ? result.length : 'Not an array');
      
      return result;
    } catch (error) {
      console.error('Error in DashboardService.getTimeClasses:', error);
      throw error;
    }
  }
}

export default DashboardService;
